import { Rect } from '../types';

export interface Size2d {
  width: number;
  height: number;
}

export function adjustToSize(
  originalSize: Size2d,
  desiredSize: Partial<Size2d>
): Size2d {
  const { width, height } = desiredSize;

  const desiredWidth = width ?? originalSize.width;
  const desiredHeight = height ?? originalSize.height;

  const scaledHeight =
    (width ?? 0) * (originalSize.height / originalSize.width || 1);
  const scaledWidth =
    (height ?? 0) * (originalSize.width / originalSize.height || 1);

  let newWidth = desiredWidth,
    newHeight = desiredHeight;

  if (width && height) {
    if (
      originalSize.width >= originalSize.height &&
      originalSize.width / originalSize.height >= width / height
    ) {
      newHeight = scaledHeight;
    } else {
      newWidth = scaledWidth;
    }

    // If image is still outside boundaries
    if (newHeight > desiredHeight || newWidth > desiredWidth) {
      // Find which scale fits better
      const heightRatio = scaledHeight / desiredHeight;
      const widthRatio = scaledWidth / desiredWidth;

      if (widthRatio <= 1) {
        newWidth = scaledWidth;
        newHeight = desiredHeight;
      } else if (heightRatio <= 1) {
        newWidth = desiredWidth;
        newHeight = scaledHeight;
      } else {
        // If neither dimension fit select which fits best and scale down
        if (widthRatio < heightRatio) {
          newWidth = scaledWidth / widthRatio;
          newHeight = desiredHeight / widthRatio;
        } else {
          newWidth = desiredWidth / heightRatio;
          newHeight = scaledHeight / heightRatio;
        }
      }
    }
  } else {
    if (width) {
      // If we have original width and height and no height explicitly set then automatically adjust height to preserve proportions
      if (!height && originalSize.width && originalSize.height) {
        newHeight = scaledHeight;
      }
    }
    if (height) {
      // If we have original width and height and no width explicitly set then automatically adjust width to preserve proportions
      if (!width && originalSize.width && originalSize.height) {
        newWidth = scaledWidth;
      }
    }
  }

  return {
    width: newWidth,
    height: newHeight,
  };
}

// Utility function to calculate intersection area between two bounding boxes
const calculateOverlapRatio = (
  box1: { x1: number; y1: number; x2: number; y2: number },
  box2: { x1: number; y1: number; x2: number; y2: number }
): number => {
  // Calculate intersection bounds
  const intersectionX1 = Math.max(box1.x1, box2.x1);
  const intersectionY1 = Math.max(box1.y1, box2.y1);
  const intersectionX2 = Math.min(box1.x2, box2.x2);
  const intersectionY2 = Math.min(box1.y2, box2.y2);

  // Check if there's any intersection
  if (intersectionX1 >= intersectionX2 || intersectionY1 >= intersectionY2) {
    return 0; // No intersection
  }

  // Calculate areas
  const intersectionArea =
    (intersectionX2 - intersectionX1) * (intersectionY2 - intersectionY1);
  const box1Area = (box1.x2 - box1.x1) * (box1.y2 - box1.y1);
  const box2Area = (box2.x2 - box2.x1) * (box2.y2 - box2.y1);

  // Return the ratio of intersection to the smaller box area
  // This ensures that a small box overlapping a large box gets a high ratio
  const smallerBoxArea = Math.min(box1Area, box2Area);
  return intersectionArea / smallerBoxArea;
};

export const areBoxOverlapping = (
  [x1, y1, w1, h1]: Rect,
  [x2, y2, w2, h2]: Rect,
  threshold = 0.1
) => {
  const rectangle1 = {
    x1,
    y1,
    x2: x1 + w1,
    y2: y1 + h1,
  };
  const rectangle2 = {
    x1: x2,
    y1: y2,
    x2: x2 + w2,
    y2: y2 + h2,
  };

  const overlapRatio = calculateOverlapRatio(rectangle1, rectangle2);

  return overlapRatio >= threshold;
};
